import { Box, Collapse, ListItem, UnorderedList, VStack } from '@televet/kibble-ui/build/chakra';
import { Alert } from '@televet/kibble-ui/build/components/Alert';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { StatusDescription } from 'pages/Reporting/records/status.records';
import { Link as ReactRouterLink } from 'react-router-dom';

import React, { useMemo, useState } from 'react';
import { Role, StripeDisputeStatus } from 'shared/types/graphql';
import { CareEnrollmentPaymentData } from './ActivityLog/types';
import { RouteBasePaths } from 'routes';
import useClinicUser from 'shared/hooks/useClinicUser';

export interface MembershipDisputeAlertProps {
  disputes?: CareEnrollmentPaymentData[];
  cancellationMode?: boolean;
  isLost?: boolean;
  handleClose?: () => void;
}

interface DescriptionProps {
  count: number;
  date: Date;
  status: StripeDisputeStatus;
  cancellationMode?: boolean;
  isLost?: boolean;
  handleClose?: () => void;
}

const Description = ({ count, date, status, cancellationMode, isLost, handleClose }: DescriptionProps): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const { clinicUser } = useClinicUser();
  const isAdmin = useMemo(() => clinicUser?.vetInfo?.roles?.some((r) => r.role === Role.Admin), [clinicUser]);

  return (
    <Box>
      <Collapse in={!isCollapsed}>
        <VStack alignItems="flex-start" spacing={1}>
          {cancellationMode && (
            <Text size="sm">
              Membership will be cancelled but payment is not eligible for a refund due to the payment being disputed by
              the client.
            </Text>
          )}
          {!cancellationMode && (
            <UnorderedList pl={1}>
              {count > 1 && (
                <ListItem>
                  <Text size="sm">
                    Total Disputes:{' '}
                    <Text size="sm" fontWeight="bold">
                      {count}
                    </Text>
                  </Text>
                </ListItem>
              )}
              {count === 1 && (
                <>
                  <ListItem>
                    <Text size="sm">
                      Dispute Date:{' '}
                      <Text size="sm" fontWeight="bold">
                        {date.toLocaleDateString()}
                      </Text>
                    </Text>
                  </ListItem>
                  <ListItem>
                    <Text size="sm">
                      Dispute Status:{' '}
                      <Text size="sm" fontWeight="bold">
                        {StatusDescription[status]}
                      </Text>
                    </Text>
                  </ListItem>
                </>
              )}
            </UnorderedList>
          )}
          {isAdmin && !isLost && (
            <Link
              as={ReactRouterLink}
              variant="default"
              to={RouteBasePaths.DisputesReporting}
              size="sm"
              iconProps={{ name: 'externalLink', size: 'sm' }}
              textDecoration="none"
              onClick={(): void => {
                if (cancellationMode && handleClose) {
                  handleClose();
                }
              }}
            >
              View Disputes Report
            </Link>
          )}
        </VStack>
      </Collapse>
      <Button onClick={(): void => setIsCollapsed((prevState) => !prevState)} variant="secondary" size="xs" mt={2}>
        {isCollapsed ? 'Show' : 'Hide'} Details
      </Button>
    </Box>
  );
};

export const MembershipDisputeAlert = ({
  disputes,
  isLost,
  cancellationMode = false,
  handleClose,
}: MembershipDisputeAlertProps): JSX.Element | null => {
  const disputesInfo = useMemo(() => {
    const activeDisputes =
      disputes?.filter(
        ({ status }) =>
          !(
            status === StripeDisputeStatus.Lost ||
            status === StripeDisputeStatus.Won ||
            status === StripeDisputeStatus.WarningClosed
          ),
      ) || [];
    return {
      date: !!activeDisputes.length ? undefined : activeDisputes[0]?.disputeDate,
      status: activeDisputes[0]?.status,
      count: activeDisputes.length,
    };
  }, [disputes]);

  if (!disputesInfo.count && !cancellationMode) {
    return null;
  }

  return (
    <Alert
      title={isLost ? 'Payment Disputed' : 'Active Payment Disputes'}
      status="error"
      description={
        <Description
          count={disputesInfo.count}
          date={disputesInfo.date || new Date()}
          status={disputesInfo.status as StripeDisputeStatus}
          cancellationMode={cancellationMode}
          handleClose={handleClose}
          isLost={isLost}
        />
      }
      titleProps={{
        fontSize: '16px',
      }}
      hideCloseButton
      mt={2}
    />
  );
};
